import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, Badge, Spinner, SimpleGrid } from "@chakra-ui/react";
import { ArrowLeft, ExternalLink, FileText, MapPin, CalendarClock, User, Users, BellRing } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { apiPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { showToast } from "@/components/AppToaster";
import { SignerList, ACK_META, ackStatus, signedCounts } from "@/components/AcknowledgeModal";
import { colors } from "@/theme/colors";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtTs = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

const MetaItem = ({ icon, label, value }) => (
  <Box>
    <HStack gap="1.5" mb="0.5">
      <Icon as={icon} boxSize="3.5" color={colors.textSecondary} />
      <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" color={colors.textSecondary}>{label}</Text>
    </HStack>
    <Text fontSize="sm" color={colors.textPrimary}>{value || "—"}</Text>
  </Box>
);

export default function AcknowledgementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, activeCompanyId, isSuperAdmin } = useAuth();
  const isManager = ["org_admin", "supervisor"].includes(role);
  const [loading, setLoading] = useState(true);
  const [a, setA] = useState(null);
  const [resource, setResource] = useState(null);
  const [group, setGroup] = useState([]);
  const [reminding, setReminding] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from("form_assignments").select("*, assignment_signoffs(*)").eq("id", id).maybeSingle();
    setA(data || null);
    if (data?.resource_id) {
      const { data: r } = await supabase.from("resources").select("id,title,file_name,file_path,file_type,version").eq("id", data.resource_id).maybeSingle();
      setResource(r || null);
    }
    if (data?.series_id) {
      // Roll-up: every assignee in this request batch (same series + occurrence).
      const { data: g } = await supabase.from("form_assignments")
        .select("id,worker_name,status,due_date,completed_at")
        .eq("series_id", data.series_id).eq("due_date", data.due_date).eq("kind", "resource")
        .order("worker_name");
      setGroup(g || []);
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const viewDoc = async () => {
    const path = a?.resource_file_path || resource?.file_path;
    const { data, error } = await supabase.storage.from("resources").createSignedUrl(path, 300);
    if (error || !data?.signedUrl) { setErr("Could not open the document — please try again."); return; }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  const remind = async () => {
    setReminding(true);
    try {
      const res = await apiPost(`/assignments/${a.id}/remind`, { redirect_to: window.location.origin });
      showToast(res.reminded === 0
        ? "No one is outstanding — everyone has signed."
        : `Reminded ${res.reminded} outstanding ${res.reminded === 1 ? "person" : "people"}${res.emailed ? ` (${res.emailed} emailed)` : ""}.`);
    } catch (e) { setErr(e.message); } finally { setReminding(false); }
  };

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;
  if (!a) return <Text color={colors.textSecondary} data-testid="ack-not-found">Acknowledgement not found.</Text>;

  // Acting-company boundary (Sweep A5): RLS may let a multi-membership user
  // read this record, but it must never render outside the company they're
  // acting in. DELIBERATE exception: Super-Admins may open any company's record.
  if (!isSuperAdmin && a.company_id && activeCompanyId && a.company_id !== activeCompanyId) {
    return (
      <Box data-testid="ack-wrong-company">
        <Text fontWeight="600" color={colors.textPrimary}>This record belongs to another of your companies.</Text>
        <Text mt="1" fontSize="sm" color={colors.textSecondary}>
          You're currently working in a different company, so it can't be shown here.
        </Text>
        <Button mt="4" size="sm" variant="outline" onClick={() => navigate("/submissions")}>
          <ArrowLeft size={15} /> Back to submissions
        </Button>
      </Box>
    );
  }

  const st = ackStatus(a);
  const meta = ACK_META[st];
  const { signed, total } = signedCounts(a);
  const signoffs = [...(a.assignment_signoffs || [])].sort((x, y) => (x.signer_role === "assignee" ? -1 : 1) - (y.signer_role === "assignee" ? -1 : 1));
  const docAvailable = Boolean(a.resource_file_path || resource?.file_path);
  const acked = group.filter((g) => g.status === "completed").length;
  const outstanding = group.filter((g) => !["completed", "superseded"].includes(g.status)).map((g) => g.worker_name);

  return (
    <Box data-testid="ack-detail-page" maxW="780px">
      <Button size="xs" variant="ghost" onClick={() => navigate("/submissions")} mb="3" data-testid="ack-back-btn">
        <ArrowLeft size={14} /> Back to submissions
      </Button>

      <Flex justify="space-between" align="flex-start" gap="4" flexWrap="wrap" mb="5" data-testid="ack-detail-header">
        <Box>
          <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "xl", md: "2xl" }} color={colors.textPrimary}>{a.title}</Heading>
          <HStack gap="2" mt="1.5" flexWrap="wrap">
            <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5" data-testid="ack-type-badge">Document acknowledgement</Badge>
            {a.resource_version && (
              <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2.5" data-testid="ack-detail-version">v{a.resource_version}</Badge>
            )}
            <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2.5" data-testid="ack-status-badge">
              {st === "awaiting_signatures" ? `Awaiting signatures (${signed} of ${total} signed)` : meta.label}
            </Badge>
          </HStack>
        </Box>
      </Flex>

      {st === "superseded" && (
        <Box mb="4" px="4" py="3" borderRadius="10px" bg="#ECECEC" color={colors.textSecondary} fontSize="sm" data-testid="ack-superseded-banner">
          The document was updated to a newer version after this request was sent — a fresh acknowledgement was automatically re-requested. This record is kept as history.
        </Box>
      )}

      {err && <Box mb="4" px="4" py="3" borderRadius="10px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm">{err}</Box>}

      <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="ack-detail-meta">
        <SimpleGrid columns={{ base: 2, md: 3 }} gap="4">
          <MetaItem icon={User} label="Assignee" value={a.worker_name} />
          <MetaItem icon={MapPin} label="Location" value={a.location_name} />
          <MetaItem icon={CalendarClock} label="Due date" value={fmtDate(a.due_date)} />
          <MetaItem icon={User} label="Scheduled by" value={a.assigned_by_name} />
          <MetaItem icon={CalendarClock} label="Scheduled at" value={fmtTs(a.created_at)} />
          <MetaItem icon={CalendarClock} label="Completed at" value={a.completed_at ? fmtTs(a.completed_at) : "—"} />
        </SimpleGrid>
        {a.instructions && <Text fontSize="sm" color={colors.textSecondary} mt="4" fontStyle="italic">Instructions: "{a.instructions}"</Text>}
      </Box>

      <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="ack-detail-document">
        <HStack gap="2" mb="3">
          <Icon as={FileText} boxSize="4" color={colors.teal} />
          <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Acknowledged document</Text>
        </HStack>
        {resource || docAvailable ? (
          <Flex justify="space-between" align="center" gap="3" flexWrap="wrap">
            <Box>
              <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{resource?.title || a.title}</Text>
              <Text fontSize="xs" color={colors.textSecondary}>
                {resource?.file_name || "Document snapshot"} · acknowledged version: v{a.resource_version || 1}
                {resource?.version && resource.version !== (a.resource_version || 1) ? ` (current library version: v${resource.version})` : ""}
              </Text>
            </Box>
            <Button size="sm" variant="outline" onClick={viewDoc} disabled={!docAvailable} data-testid="ack-detail-view-doc">
              <ExternalLink size={14} /> View document
            </Button>
          </Flex>
        ) : (
          <Text fontSize="sm" color={colors.textSecondary}>The original document is no longer in the library.</Text>
        )}
      </Box>

      {group.length > 1 && (
        <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" mb="4" data-testid="ack-rollup">
          <Flex justify="space-between" align="center" mb="3" gap="3" flexWrap="wrap">
            <HStack gap="2">
              <Icon as={Users} boxSize="4" color={colors.teal} />
              <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>
                Group request — {acked} of {group.length} acknowledged
              </Text>
            </HStack>
            {isManager && outstanding.length > 0 && st !== "superseded" && (
              <Button size="sm" bg={colors.sageDeep} _hover={{ bg: colors.sage }} onClick={remind}
                loading={reminding} data-testid="ack-remind-btn">
                <BellRing size={14} /> Remind outstanding ({outstanding.length})
              </Button>
            )}
          </Flex>
          <Stack gap="1.5">
            {group.map((g) => {
              const gm = ACK_META[ackStatus(g)];
              return (
                <Flex key={g.id} justify="space-between" align="center" gap="3" data-testid={`rollup-row-${g.id}`}>
                  <Text fontSize="sm" color={colors.textPrimary} cursor={g.id !== a.id ? "pointer" : "default"}
                    textDecoration={g.id !== a.id ? "underline" : "none"}
                    onClick={() => g.id !== a.id && navigate(`/acknowledgements/${g.id}`)}>
                    {g.worker_name}{g.id === a.id ? " (this record)" : ""}
                  </Text>
                  <Badge bg={gm.bg} color={gm.color} borderRadius="full" px="2.5" data-testid={`rollup-status-${g.id}`}>{gm.label}</Badge>
                </Flex>
              );
            })}
          </Stack>
          {outstanding.length > 0 && (
            <Text fontSize="xs" color={colors.textSecondary} mt="3" data-testid="rollup-outstanding">
              Outstanding: {outstanding.join(", ")}
            </Text>
          )}
        </Box>
      )}

      <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" data-testid="ack-detail-signers">
        <Flex justify="space-between" align="center" mb="3">
          <Text fontSize="sm" fontWeight="700" color={colors.textPrimary}>Required signatures</Text>
          <Text fontSize="xs" color={colors.textSecondary}>{signed} of {total} signed</Text>
        </Flex>
        <SignerList signoffs={signoffs} showSignatures />
      </Box>
    </Box>
  );
}
